import { useMemo } from 'react';
import { List } from 'immutable';
import { formatReportValue, snakeCaseToTitleCase } from 'src/utils/reportUtils';

/**
 * Determines if combined controller/circuit column should be shown
 * @param firstRow - First row of data to check for fields
 * @returns True if (tag or circuit_tag) exists - shows combined column even if controller_tag is missing
 */
export const shouldShowCombinedColumn = (firstRow: any): boolean => {
  if (!firstRow) return false;
  const hasCircuitTag = 'tag' in firstRow || 'circuit_tag' in firstRow;
  // Show combined column if circuit tag exists (even without controller_tag)
  // The combined column can handle displaying just the circuit tag
  return hasCircuitTag;
};

/**
 * Builds dynamic columns from data fields
 * @param fieldNames - Array of field names from data
 * @param columnDefinitions - Predefined column definitions
 * @param getFieldLabelFn - Function to get field labels
 * @param shouldShowCombined - Whether combined column is shown
 * @returns Array of column definitions
 */
export const buildDynamicColumns = (
  fieldNames: string[],
  columnDefinitions: Record<string, any>,
  getFieldLabelFn: (fieldName: string) => string,
  shouldShowCombined: boolean
): any[] => {
  return fieldNames.map(fieldName => {
    // Use predefined column definition if available
    let colDef = columnDefinitions[fieldName];

    // If showing combined column, hide individual controller_tag and tag/circuit_tag columns
    // (but keep them in the grid for export purposes)
    if (shouldShowCombined && (fieldName === 'controller_tag' || fieldName === 'tag' || fieldName === 'circuit_tag')) {
      if (colDef) {
        colDef = { ...colDef, hide: true };
      } else {
        // Create column definition for hidden columns
        const headerName = getFieldLabelFn(fieldName) || snakeCaseToTitleCase(fieldName);
        return {
          field: fieldName,
          headerName: headerName,
          minWidth: 100,
          hide: true,
          flex: 1
        };
      }
    }

    if (colDef) {
      return colDef;
    }

    // For unknown fields, use custom label if available, otherwise convert snake_case to Title Case
    const headerName = getFieldLabelFn(fieldName) || snakeCaseToTitleCase(fieldName);

    return {
      field: fieldName,
      headerName: headerName,
      minWidth: 100,
      valueFormatter: (params: any) => formatReportValue(params.value),
      flex: 1
    };
  });
};

/**
 * Hook for dynamic column generation with client preferences support
 * @param data - Report data (Immutable List)
 * @param columnDefinitions - Predefined column definitions
 * @param getFieldLabelFn - Function to get field labels
 * @param combinedColumnDef - Combined column definition (if applicable)
 * @returns Array of column definitions
 */
export const useReportColumns = (
  data: List<any> | null,
  columnDefinitions: Record<string, any>,
  getFieldLabelFn: (fieldName: string) => string,
  combinedColumnDef?: any
): any[] => {
  return useMemo(() => {
    if (!data || data.size === 0) {
      // If no data, return default columns
      return Object.values(columnDefinitions);
    }

    const dataArray = data.toJS();
    const firstRow = dataArray[0];
    if (!firstRow) {
      return Object.values(columnDefinitions);
    }

    // Check if we should show combined controller/circuit column
    const shouldShowCombined = shouldShowCombinedColumn(firstRow);

    // Get all field names from the first row
    const fieldNames = Object.keys(firstRow);

    // Build columns based on available fields
    const dynamicColumns = buildDynamicColumns(
      fieldNames,
      columnDefinitions,
      getFieldLabelFn,
      shouldShowCombined
    );

    // If we should show combined column, add it at the beginning
    if (shouldShowCombined && combinedColumnDef) {
      dynamicColumns.unshift(combinedColumnDef);
    }

    return dynamicColumns;
  }, [data, columnDefinitions, getFieldLabelFn, combinedColumnDef]);
};
